import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { DepartureCheckService } from 'src/app/services/departure-check.service';
import { UtilsService } from 'src/app/services/utils.service';
import { environment } from 'src/environments/environment';
import { DamagePoint } from '../vehicle-damage-diagram/vehicle-damage-diagram.component';

interface FuncionamientoItem {
    key: string;
    label: string;
    icon: string;
    ok: boolean | null;
}

interface SensorItem {
    key: string;
    label: string;
    icon: string;
    w: number;
    h: number;
    active: boolean;
}

interface DocumentoItem {
    key: string;
    label: string;
    subtitle: string;
    icon: string;
    boxClass: string;
    verified: boolean;
}

const STEP_LABELS = ['Servicio', 'Unidad', 'Funcionamiento', 'Tablero', 'Fotos', 'Documentos', 'Resumen'];

/** Opcion final, excluyente con el resto, en cada lista de anomalias. */
const NINGUNA_ANOMALIA = 'Ninguna anomalía';

/**
 * Opciones de anomalia por elemento de Funcionamiento (paso 3, pantalla
 * "¿Que anomalia presenta?").
 */
const FUNCIONAMIENTO_ANOMALY_OPTIONS: Record<string, string[]> = Object.fromEntries(
    Object.entries({
        keys_ok: ['No gira / se atora', 'No enciende el vehículo', 'No abre las puertas', 'Control remoto no funciona', 'Llave dañada / doblada', 'Otro'],
        levers_ok: ['No funciona', 'Funciona de forma intermitente', 'Se atora', 'Está floja', 'No regresa a su posición', 'Dañada / rota', 'Otro'],
        headlights_ok: ['No funciona (fundido)', 'Intermitente no funciona', 'Luz baja no funciona', 'Luz alta no funciona', 'Cristal roto/dañado', 'Opaco / desgastado', 'Otro'],
        tires_ok: ['Baja presión', 'Desgaste excesivo', 'Desgaste irregular', 'Ponchadura', 'Deformación / abultamiento', 'Otro'],
        ignition_ok: ['No funciona', 'No genera corriente', 'Conector flojo', 'Dañado / roto', 'Se calienta demasiado', 'Otro'],
        fire_extinguisher_ok: ['Faltante', 'Vencido', 'Descargado', 'Manómetro fuera de rango', 'Sello de seguridad dañado', 'Soporte flojo / dañado', 'Otro'],
        mirrors_ok: ['Espejo roto / estrellado', 'Espejo desprendido', 'Espejo flojo', 'No permite ajuste', 'Ajuste eléctrico no funciona', 'Cristal opaco / deteriorado', 'Otro'],
        upholstery_ok: ['Roto', 'Manchado', 'Desgastado', 'Quemado', 'Descosido', 'Desprendido', 'Otro'],
        accessories_ok: ['Faltante', 'Dañado', 'Roto', 'Flojo / desprendido', 'No funciona', 'Otro'],
    }).map(([key, options]) => [key, [...options, NINGUNA_ANOMALIA]])
);

@Component({
    selector: 'app-departure-check-wizard',
    templateUrl: './departure-check-wizard.component.html',
    styleUrls: ['./departure-check-wizard.component.css']
})
export class DepartureCheckWizardComponent implements OnInit {
    stepLabels = STEP_LABELS;
    step = 1;
    checkId!: number;
    loading = false;
    saving = false;

    check: any = null;

    // Paso 1
    /** Casillas de verificacion "Rectifica con el Operador los datos" (solo
     * visual/local: no hay columna en el backend para persistir esto). */
    verifiedFields: Record<string, boolean> = {};
    serviceObservations = '';

    // Paso 2
    damagePoints: DamagePoint[] = [];
    damageObservations = '';

    // Paso 3
    funcionamiento: FuncionamientoItem[] = [
        { key: 'keys_ok', label: 'Llaves', icon: 'icon-key.svg', ok: null },
        { key: 'levers_ok', label: 'Palancas', icon: 'icon-levers.svg', ok: null },
        { key: 'headlights_ok', label: 'Faros', icon: 'icon-headlights.svg', ok: null },
        { key: 'tires_ok', label: 'Neumáticos', icon: 'icon-tires.svg', ok: null },
        { key: 'ignition_ok', label: 'Encendedor', icon: 'icon-ignition.svg', ok: null },
        { key: 'fire_extinguisher_ok', label: 'Extintor', icon: 'icon-fire-extinguisher.svg', ok: null },
        { key: 'mirrors_ok', label: 'Espejos', icon: 'icon-mirrors.svg', ok: null },
        { key: 'upholstery_ok', label: 'Tapizados', icon: 'icon-upholstery.svg', ok: null },
        { key: 'accessories_ok', label: 'Accesorios', icon: 'icon-accessories.svg', ok: null },
    ];
    funcionamientoAnomalyOptions = FUNCIONAMIENTO_ANOMALY_OPTIONS;
    funcionamientoAnomalies: Record<string, { anomalies: string[], note: string }> = {};
    funcionamientoSubStep: 'list' | 'detail' = 'list';
    selectedFuncionamientoItem: FuncionamientoItem | null = null;
    detailAnomalyNote = '';

    // Paso 4
    mileage: number | null = null;
    fuelLevel = 50;
    /** Marcas del medidor: -90deg = E, 0deg = 1/2 (arriba), 90deg = F. */
    odometerPhotoFile: File | null = null;
    odometerPhotoPreview: string | null = null;
    tableroObservations = '';
    sensoresRow1: SensorItem[] = [
        { key: 'cinturon', label: 'Cinturón de seguridad', icon: 'icon-sensor-seatbelt.svg', w: 50, h: 55, active: false },
        { key: 'motor', label: 'Motor', icon: 'icon-sensor-engine.svg', w: 67, h: 48.418, active: false },
        { key: 'abs', label: 'ABS', icon: 'icon-sensor-abs.svg', w: 64, h: 48, active: false },
        { key: 'aceite', label: 'Aceite', icon: 'icon-sensor-oil.svg', w: 81.4789, h: 34, active: false },
        { key: 'bateria', label: 'Batería', icon: 'icon-sensor-battery.svg', w: 56.4122, h: 37, active: false },
        { key: 'airbag', label: 'Airbag', icon: 'icon-sensor-airbag.svg', w: 38, h: 44, active: false },
        { key: 'alerta', label: 'Alerta general', icon: 'icon-sensor-warning.svg', w: 59.6, h: 44, active: false },
    ];
    sensoresRow2: SensorItem[] = [
        { key: 'luces', label: 'Luces', icon: 'icon-sensor-headlight.svg', w: 55.3901, h: 41.9996, active: false },
        { key: 'asientos', label: 'Sensor de asiento', icon: 'icon-sensor-seat.svg', w: 43.4983, h: 55.0005, active: false },
        { key: 'limpiaparabrisas', label: 'Limpiaparabrisas', icon: 'icon-sensor-wash.svg', w: 51, h: 43.8145, active: false },
    ];
    get sensores(): SensorItem[] {
        return [...this.sensoresRow1, ...this.sensoresRow2];
    }

    // Paso 5
    fotoCategorias = [
        { key: 'frontal', label: 'Frontal' },
        { key: 'lateral_izquierda', label: 'Lateral izquierda' },
        { key: 'lateral_derecha', label: 'Lateral derecha' },
        { key: 'trasera', label: 'Trasera' },
    ];
    fotoFiles: Record<string, File> = {};
    fotoPreviews: Record<string, string> = {};

    // Paso 6
    documentos: DocumentoItem[] = [];
    documentosObservations = '';

    // Paso 7
    generalObservations = '';
    /** Solo relevante cuando !unidadApta: confirmacion explicita del
     * operador de si aun asi se libera la unidad o se solicita corregir
     * el servicio. null = aun no decide. */
    unidadAptaConfirmation: boolean | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private departureCheckService: DepartureCheckService,
        private spinner: NgxSpinnerService,
        private utils: UtilsService
    ) { }

    private isResume = false;

    ngOnInit(): void {
        this.checkId = Number(this.route.snapshot.paramMap.get('id'));
        this.isResume = this.route.snapshot.queryParamMap.has('resume');
        this.loadCheck();
    }

    private loadCheck(): void {
        this.loading = true;
        this.spinner.show();
        this.departureCheckService.getCheck(this.checkId).subscribe({
            next: (resp: any) => {
                this.check = resp;
                this.hydrateFromCheck(resp);
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.loading = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar el check de salida', icon: 'error' });
                this.router.navigateByUrl('/unit/departure-checks');
            }
        });
    }

    private hydrateFromCheck(check: any): void {
        this.verifiedFields = check.service_verified_fields || {};
        this.serviceObservations = check.service_observations || '';

        this.damagePoints = check.damage_points || [];
        this.damageObservations = check.damage_observations || '';

        this.funcionamiento.forEach(item => {
            item.ok = check.funcionamiento?.[item.key] ?? null;
        });
        this.funcionamientoAnomalies = check.funcionamiento_anomalies || {};

        this.mileage = check.mileage;
        this.fuelLevel = check.fuel_level ?? 50;
        this.tableroObservations = check.tablero_observations || '';
        const activeSensors: string[] = check.dashboard_sensors || [];
        this.sensores.forEach(s => s.active = activeSensors.includes(s.key));

        const photoByCategory: Record<string, any> = {};
        (check.photos || []).forEach((p: any) => photoByCategory[p.category] = p);
        if (photoByCategory['odometro']) {
            this.odometerPhotoPreview = environment.url_page + photoByCategory['odometro'].url;
        }
        this.fotoCategorias.forEach(c => {
            if (photoByCategory[c.key]) {
                this.fotoPreviews[c.key] = environment.url_page + photoByCategory[c.key].url;
            }
        });

        const svc = check.service || {};
        this.documentos = [
            { key: 'insurance_verified', label: 'Póliza de seguro (Impresa)', subtitle: svc.insurer || '--', icon: 'icon-shield-check.svg', boxClass: 'dc-doc-box--teal', verified: !!check.documentos?.insurance_verified },
            { key: 'license_verified', label: 'Licencia de conducir (Operador)', subtitle: svc.driver_name || '--', icon: 'icon-id-card-license.svg', boxClass: 'dc-doc-box--orange', verified: !!check.documentos?.license_verified },
            { key: 'id_verified', label: 'Identificación oficial (Operador)', subtitle: svc.driver_name || '--', icon: 'icon-id-card-official.svg', boxClass: 'dc-doc-box--blue', verified: !!check.documentos?.id_verified },
            { key: 'registration_verified', label: 'Tarjeta de circulación (Unidad)', subtitle: (svc.unit || '') + (svc.unit_configuration ? ' (' + svc.unit_configuration + ')' : ''), icon: 'icon-id-card-registration.svg', boxClass: 'dc-doc-box--purple', verified: !!check.documentos?.registration_verified },
        ];
        this.documentosObservations = check.documentos_observations || '';
        this.generalObservations = check.general_observations || '';

        if (this.isResume) {
            // Preferir el paso literal donde el usuario se quedo (persistido
            // via goToStep); si el check es anterior a que existiera ese
            // campo, se infiere a partir de que datos ya estan guardados.
            this.step = check.current_step || this.determineResumeStep(check);
        }
    }

    /** Al reanudar un check "en progreso" desde el listado, ubica al usuario
     * en el primer paso que aun no tiene informacion guardada, en vez de
     * reiniciar siempre desde el paso 1. */
    private determineResumeStep(check: any): number {
        const isSet = (v: any) => v !== null && v !== undefined;

        if (!isSet(check.damage_points)) return 2;

        const funcionamiento = check.funcionamiento || {};
        const funcionamientoKeys = ['keys_ok', 'headlights_ok', 'ignition_ok', 'mirrors_ok', 'accessories_ok', 'levers_ok', 'tires_ok', 'fire_extinguisher_ok', 'upholstery_ok'];
        if (!funcionamientoKeys.some(k => isSet(funcionamiento[k]))) return 3;

        // fuel_level siempre se envia (tiene un valor por defecto en el slider),
        // a diferencia de mileage que el usuario puede dejar vacio.
        if (!isSet(check.fuel_level)) return 4;

        const photoCategories = new Set((check.photos || []).map((p: any) => p.category));
        const fotosCompletas = this.fotoCategorias.every(c => photoCategories.has(c.key));
        if (!fotosCompletas) return 5;

        const docs = check.documentos || {};
        const documentoKeys = ['insurance_verified', 'license_verified', 'id_verified', 'registration_verified'];
        if (!documentoKeys.some(k => isSet(docs[k]))) return 6;

        return 7;
    }

    stepStatus(index: number): 'done' | 'active' | 'pending' {
        const stepNumber = index + 1;
        if (stepNumber < this.step) return 'done';
        if (stepNumber === this.step) return 'active';
        return 'pending';
    }

    /** Cambia de paso y lo persiste como current_step, para que "Continuar
     * check" reanude exactamente en el paso donde se quedo el usuario (no
     * solo el ultimo con datos guardados) — cubre tambien "Regresar", que
     * de otro modo es puramente local y no se reflejaria al reanudar. */
    private goToStep(step: number): void {
        this.step = step;
        this.departureCheckService.updateStep(this.checkId, step).subscribe({ error: () => { } });
    }

    regresar(): void {
        if (this.step === 1) {
            this.router.navigateByUrl('/unit/departure-checks');
            return;
        }
        this.goToStep(this.step - 1);
    }

    /** Flecha del encabezado: sale del flujo hacia el listado, sin importar
     * el paso actual (distinto del boton "Regresar" de cada paso, que
     * retrocede dentro del formulario). */
    salirAlListado(): void {
        this.router.navigateByUrl('/unit/departure-checks');
    }

    cancelar(): void {
        this.router.navigateByUrl('/unit/departure-checks');
    }

    // ---- Paso 1 ----
    toggleVerified(key: string): void {
        this.verifiedFields[key] = !this.verifiedFields[key];
    }

    continuarServicio(): void {
        this.saving = true;
        this.departureCheckService.updateServicio(this.checkId, {
            service_verified_fields: this.verifiedFields,
            service_observations: this.serviceObservations,
        }).subscribe({
            next: (resp: any) => { this.check = resp; this.saving = false; this.goToStep(2); },
            error: () => { this.saving = false; this.showSaveError(); }
        });
    }

    // ---- Paso 2 ----
    onDamagePointsChange(points: DamagePoint[]): void {
        this.damagePoints = points;
    }

    continuarDamage(): void {
        this.saving = true;
        this.departureCheckService.updateDamage(this.checkId, {
            damage_points: this.damagePoints,
            damage_observations: this.damageObservations,
        }).subscribe({
            next: (resp: any) => { this.check = resp; this.saving = false; this.goToStep(3); },
            error: () => { this.saving = false; this.showSaveError(); }
        });
    }

    // ---- Paso 3 ----
    /** Tocar un elemento abre "¿Que anomalia presenta?" para ese elemento. */
    abrirDetalleFuncionamiento(item: FuncionamientoItem): void {
        this.selectedFuncionamientoItem = item;
        const existing = this.funcionamientoAnomalies[item.key];
        this.detailAnomalyNote = existing?.note || '';
        this.funcionamientoSubStep = 'detail';
    }

    cerrarDetalleFuncionamiento(): void {
        this.funcionamientoSubStep = 'list';
        this.selectedFuncionamientoItem = null;
    }

    anomalyOptionsFor(item: FuncionamientoItem): string[] {
        return this.funcionamientoAnomalyOptions[item.key] || [];
    }

    isAnomalySelected(itemKey: string, option: string): boolean {
        return !!this.funcionamientoAnomalies[itemKey]?.anomalies?.includes(option);
    }

    /** "Ninguna anomalia" es excluyente: al marcarla se limpian las demas, y
     * al marcar cualquier otra se desmarca "Ninguna anomalia". */
    toggleAnomalyOption(itemKey: string, option: string): void {
        const current = this.funcionamientoAnomalies[itemKey] || { anomalies: [], note: '' };
        let anomalies: string[];
        if (option === NINGUNA_ANOMALIA) {
            anomalies = current.anomalies.includes(option) ? [] : [option];
        } else if (current.anomalies.includes(option)) {
            anomalies = current.anomalies.filter(a => a !== option);
        } else {
            anomalies = [...current.anomalies.filter(a => a !== NINGUNA_ANOMALIA), option];
        }
        this.funcionamientoAnomalies[itemKey] = { ...current, anomalies };
    }

    /** Guarda las anomalias seleccionadas (o "sin anomalias" si no se marco ninguna) y regresa a la lista. */
    registrarAnomalia(): void {
        const item = this.selectedFuncionamientoItem;
        if (!item) return;
        const current = this.funcionamientoAnomalies[item.key];
        const anomalies = current?.anomalies || [];
        this.funcionamientoAnomalies[item.key] = { anomalies, note: this.detailAnomalyNote };
        item.ok = anomalies.length === 0 || anomalies.every(a => a === NINGUNA_ANOMALIA);
        this.cerrarDetalleFuncionamiento();
    }

    get funcionamientoCompletos(): number {
        return this.funcionamiento.filter(f => f.ok === true).length;
    }

    get funcionamientoRevisados(): number {
        return this.funcionamiento.filter(f => f.ok !== null).length;
    }

    continuarFuncionamiento(): void {
        const payload: any = { funcionamiento_anomalies: this.funcionamientoAnomalies };
        this.funcionamiento.forEach(item => payload[item.key] = item.ok);
        this.saving = true;
        this.departureCheckService.updateFuncionamiento(this.checkId, payload).subscribe({
            next: (resp: any) => { this.check = resp; this.saving = false; this.goToStep(4); },
            error: () => { this.saving = false; this.showSaveError(); }
        });
    }

    // ---- Paso 4 ----
    get needleAngle(): number {
        return (this.fuelLevel / 100) * 180 - 90;
    }

    onOdometerPhotoSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        this.odometerPhotoFile = file;
        const reader = new FileReader();
        reader.onload = () => this.odometerPhotoPreview = reader.result as string;
        reader.readAsDataURL(file);
    }

    toggleSensor(sensor: SensorItem): void {
        sensor.active = !sensor.active;
    }

    continuarTablero(): void {
        this.saving = true;
        const activeSensors = this.sensores.filter(s => s.active).map(s => s.key);
        this.departureCheckService.updateTablero(this.checkId, {
            mileage: this.mileage,
            fuel_level: this.fuelLevel,
            dashboard_sensors: activeSensors,
            tablero_observations: this.tableroObservations,
        }, this.odometerPhotoFile || undefined).subscribe({
            next: (resp: any) => { this.check = resp; this.saving = false; this.goToStep(5); },
            error: () => { this.saving = false; this.showSaveError(); }
        });
    }

    // ---- Paso 5 ----
    onFotoSelected(event: Event, categoria: string): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        this.fotoFiles[categoria] = file;
        const reader = new FileReader();
        reader.onload = () => this.fotoPreviews[categoria] = reader.result as string;
        reader.readAsDataURL(file);
    }

    get fotosCompletas(): number {
        return this.fotoCategorias.filter(c => this.fotoPreviews[c.key]).length;
    }

    continuarFotos(): void {
        const faltantes = this.fotoCategorias.filter(c => !this.fotoPreviews[c.key] && !this.fotoFiles[c.key]);
        if (faltantes.length > 0) {
            this.utils.alertSwal({
                title: 'Evidencia fotográfica incompleta',
                text: 'Toma las fotos requeridas antes de continuar: ' + faltantes.map(c => c.label).join(', ') + '.',
                icon: 'warning'
            });
            return;
        }
        // La foto de odometro se puede reemplazar aqui aunque ya se haya
        // tomado en el paso 4 (Tablero); se guarda junto con el resto.
        if (this.odometerPhotoFile) {
            this.fotoFiles['odometro'] = this.odometerPhotoFile;
        }
        if (Object.keys(this.fotoFiles).length === 0) {
            this.goToStep(6);
            return;
        }
        this.saving = true;
        this.departureCheckService.updateFotos(this.checkId, this.fotoFiles).subscribe({
            next: (resp: any) => { this.check = resp; this.fotoFiles = {}; this.saving = false; this.goToStep(6); },
            error: () => { this.saving = false; this.showSaveError(); }
        });
    }

    // ---- Paso 6 ----
    toggleDocumento(doc: DocumentoItem): void {
        doc.verified = !doc.verified;
    }

    continuarDocumentos(): void {
        const payload: any = { documentos_observations: this.documentosObservations };
        this.documentos.forEach(d => payload[d.key] = d.verified);
        this.saving = true;
        this.departureCheckService.updateDocumentos(this.checkId, payload).subscribe({
            next: (resp: any) => { this.check = resp; this.saving = false; this.goToStep(7); },
            error: () => { this.saving = false; this.showSaveError(); }
        });
    }

    // ---- Paso 7 ----
    get resumenServicio(): string { return 'Completado'; }
    get resumenUnidad(): string {
        return this.damagePoints.length > 0 ? `${this.damagePoints.length} daños registrados` : 'Sin daños registrados';
    }
    get resumenFuncionamiento(): string {
        return `${this.funcionamientoCompletos}/${this.funcionamiento.length} - ${this.funcionamientoCompletos === this.funcionamiento.length ? 'OK' : 'Revisar'}`;
    }
    get resumenSensores(): string {
        const active = this.sensores.filter(s => s.active).length;
        return active === 0 ? 'Sin alertas' : `${active} alertas registradas`;
    }
    get resumenFotos(): string {
        return `${this.fotosCompletas}/${this.fotoCategorias.length} fotos`;
    }
    get resumenDocumentos(): string {
        return this.documentos.every(d => d.verified) ? 'Completa' : 'Incompleta';
    }
    get unidadApta(): boolean {
        return this.damagePoints.length === 0
            && this.funcionamientoCompletos === this.funcionamiento.length
            && this.sensores.every(s => !s.active)
            && this.documentos.every(d => d.verified);
    }

    liberarUnidad(): void {
        this.saving = true;
        this.spinner.show();
        this.departureCheckService.complete(this.checkId, this.generalObservations).subscribe({
            next: (resp: any) => {
                this.saving = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Unidad liberada', text: 'El check de salida se completó correctamente', icon: 'success' });
                this.router.navigateByUrl(`/unit/departure-checks/${resp.id}/resumen`);
            },
            error: () => {
                this.saving = false;
                this.spinner.hide();
                this.showSaveError();
            }
        });
    }

    /** Paso 7, unidad no apta + confirmo "No": deja el servicio "En revisión"
     * y navega a editarlo (mismo patron que order-detail's changedUnit()). */
    solicitarActualizacion(): void {
        this.saving = true;
        this.spinner.show();
        this.departureCheckService.requestUpdate(this.checkId, this.generalObservations).subscribe({
            next: () => {
                this.saving = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Actualización solicitada', text: 'El servicio quedó en revisión.', icon: 'info' });
                this.router.navigateByUrl('/unit/departure-checks');
            },
            error: () => {
                this.saving = false;
                this.spinner.hide();
                this.showSaveError();
            }
        });
    }

    private showSaveError(): void {
        this.utils.alertSwal({ title: 'Error', text: 'No se pudo guardar la información de este paso', icon: 'error' });
    }
}
